const TIME_ZONE_SUFFIX_RE = /(?:Z|[+-]\d{2}:?\d{2})$/;

export const parseUtcDate = (value: string): Date => {
  const normalized = value.trim();
  return new Date(TIME_ZONE_SUFFIX_RE.test(normalized) ? normalized : `${normalized}Z`);
};

/** `yyyy-mm-dd` for a local calendar day. */
export const dayKeyForDate = (date: Date): string => {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * The day a UTC timestamp falls on in the viewer's timezone. Every surface renders timestamps
 * locally, so a day has to mean the day shown on the row.
 */
export const localDayKey = (value: string): string => dayKeyForDate(parseUtcDate(value));

/** A recording's length in seconds, never negative. */
export const recordingDurationSeconds = (recording: { date_started: string; date_ended: string }) =>
  Math.max(
    0,
    (parseUtcDate(recording.date_ended).getTime() -
      parseUtcDate(recording.date_started).getTime()) /
      1000,
  );

/**
 * Past ten minutes the seconds are noise (a reading built from a handful of observations cannot
 * carry them), so anything longer rounds to the minute and drops them.
 */
const SECONDS_SHOWN_BELOW = 10 * 60;

/** Past a hundred hours the minutes are noise in the same way, so longer totals round to the hour. */
const MINUTES_SHOWN_BELOW = 100 * 60 * 60;

/**
 * The one way a duration in seconds reaches the screen. Rounding happens once, on the total, so
 * the parts can never disagree with it: rounding each part on its own is what used to print
 * "1m 60s" for 119.6s and "60s" for 59.7s.
 */
export const formatDuration = (seconds: number): string => {
  const safe = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const total =
    safe > MINUTES_SHOWN_BELOW
      ? Math.round(safe / 3600) * 3600
      : safe > SECONDS_SHOWN_BELOW
        ? Math.round(safe / 60) * 60
        : Math.round(safe);
  if (total < 60) return `${total}s`;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return m === 0 ? `${h}h` : `${h}h ${m}m`;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
};
